import { CAPABILITY_PERMISSION_NAMES, collectApis, collectCapabilities } from './capabilities.js';
import { pushCollectIncident } from '/fp/collect-incidents.js';
import { collectEngineSpecific } from './engines/specific.js';
import { collectMediaDevices } from './media.js';
import { collectSensors } from './sensors.js';
import { withTiming } from '/utils.js';

export function getMimeTypes() {
  const nav = window.navigator;
  if (!nav.mimeTypes || !nav.mimeTypes.length) {
    return [];
  }
  const list = [];
  for (let i = 0; i < nav.mimeTypes.length; i++) {
    const mt = nav.mimeTypes[i];
    list.push({
      type: mt?.type ?? null,
      suffixes: mt?.suffixes ?? null,
      description: mt?.description ?? null,
      enabledPlugin: mt?.enabledPlugin ? mt.enabledPlugin.name : null,
    });
  }
  return list;
}

export function negativeToFixedError() {
  const neg = parseInt('-1');
  try {
    neg.toFixed(neg);
  } catch (e) {
    return e.message;
  }
  return null;
}

/**
 * @returns {Promise<{ StorageManager?: { quota: number, usage: number }, webkitTemporaryStorage?: { quota: number, usage: number } }>}
 */
async function getStorageInfo() {
  if (navigator.storage && typeof navigator.storage.estimate === 'function') {
    try {
      const estimate = await navigator.storage.estimate();
      return { StorageManager: { quota: estimate.quota, usage: estimate.usage } };
    } catch {
      return {};
    }
  }
  // very old browsers
  if (window.webkitStorageInfo && typeof window.webkitStorageInfo.queryUsageAndQuota === 'function') {
    try {
      const { used, quota } = await new Promise((resolve, reject) => {
        window.webkitStorageInfo.queryUsageAndQuota(
          window.webkitStorageInfo.TEMPORARY,
          (usedBytes, quotaBytes) => resolve({ used: usedBytes, quota: quotaBytes }),
          reject
        );
      });
      return { webkitTemporaryStorage: { quota, usage: used } };
    } catch {
      return {};
    }
  }
  // no storage API available
  return {};
}

async function collectKeyboardLayout() {
  if (navigator.keyboard && typeof navigator.keyboard.getLayoutMap === 'function') {
    try {
      const map = await navigator.keyboard.getLayoutMap();
      const keys = [];
      for (const [code, value] of map.entries()) {
        keys.push(`${code}:${value}`);
      }
      return {
        status: 'ok',
        size: keys.length,
        keys,
      };
    } catch {
      return {
        status: 'error',
        size: 0,
        keys: [],
      };
    }
  }
  return {
    status: 'unsupported',
    size: 0,
    keys: [],
  };
}

async function collectCapabilitiesWithFallback(capDetails, otherDetails) {
  try {
    return await collectCapabilities(capDetails);
  } catch {
    let apis = {};
    try {
      apis = await (otherDetails
        ? withTiming('apis', capDetails, () => collectApis())
        : collectApis());
    } catch {
      /* keep empty apis */
    }
    const permissions = {};
    for (let i = 0; i < CAPABILITY_PERMISSION_NAMES.length; i++) {
      permissions[CAPABILITY_PERMISSION_NAMES[i]] = 'error';
    }
    return { permissions, apis };
  }
}

/**
 * @param {Record<string, unknown>} [otherDetails] Per-step ms under timing.browserDetails.other; wall-clock is other.total (set in core.js)
 * @param {{ sensors?: boolean }} [opts]
 */
export default async function collectBrowserOther(otherDetails, opts) {
  const time = (name, fn) =>
    otherDetails ? withTiming(name, otherDetails, fn) : fn();

  let browserData = {
    navigation: {},
    timing: {},
    storage: {},
    memory: {},
    media: {},
    resources: {},
    intl: {},
    document: {},
    incidents: [],
    specific: {
      errors: {
        negativeToFixed: negativeToFixedError(),
      },
    },
    timezone: new Date().getTimezoneOffset(),
  };

  const capDetails = { permissions: {} };
  const storagePromise = time('storageInfo', () => getStorageInfo());
  const mediaPromise = time('media', () => collectMediaDevices());
  const withSensors = !opts || opts.sensors !== false;
  const specificAndSensorsPromise = time('engineSpecific', () => collectEngineSpecific(browserData.specific))
    .then(async (specific) => ({
      specific,
      sensors: specific?.brave?.isBrave || !withSensors ? null : await time('sensors', () => collectSensors()),
    }));
  const keyboardPromise = time('keyboard', () => collectKeyboardLayout());
  const capabilitiesPromise = collectCapabilitiesWithFallback(capDetails, otherDetails);
  const asyncResultsPromise = Promise.all([
    storagePromise,
    mediaPromise,
    specificAndSensorsPromise,
    keyboardPromise,
    capabilitiesPromise,
  ]);

  // Async collectors are awaited below. This only prevents unhandled rejections
  // if a sync collector throws before we reach that await.
  asyncResultsPromise.catch(() => {});

  time('syncNavigationMemoryTiming', () => {
    const nav = typeof window !== 'undefined' ? window.navigation : undefined;
    if (nav?.currentEntry != null) {
      browserData['navigation'] = {
        navigationIndex: nav.currentEntry.index,
        historyLength: history.length,
        documentReferer: document.referrer,
        navigationType: performance.navigation?.type,
      };
    } else {
      browserData['navigation'] = null;
    }

    if (typeof performance.memory !== 'undefined') {
      browserData['memory'] = {
        jsHeapSizeLimit: performance.memory?.jsHeapSizeLimit,
        totalJSHeapSize: performance.memory?.totalJSHeapSize,
        usedJSHeapSize: performance.memory?.usedJSHeapSize,
      };
    } else {
      browserData['memory'] = null;
    }

    const [navigationTiming] = performance.getEntriesByType('navigation');

    if (typeof navigationTiming !== 'undefined') {
      browserData['timing'] = {
        dnsLookup: navigationTiming?.domainLookupEnd - navigationTiming?.domainLookupStart,
        connectionDuration: navigationTiming?.domainLookupEnd - navigationTiming?.connectStart,
        downloadDuration: navigationTiming?.responseEnd - navigationTiming?.fetchStart,
        requestDuration: navigationTiming?.responseStart - navigationTiming?.requestStart,
        responseDuration: navigationTiming?.responseEnd - navigationTiming?.responseStart,
        requestResponseDuration: navigationTiming?.responseEnd - navigationTiming?.responseStart,
        tlsDuration: 0,
      };

      if (navigationTiming?.secureConnectionStart > 0) {
        browserData['timing']['tls_duration'] =
          navigationTiming?.connectEnd - navigationTiming?.secureConnectionStart;
      }
    } else {
      browserData['timing'] = {};
    }
  });

  time('syncResources', () => {
    browserData['resources'] = {
      cssFromStyleSheets: 0,
      imgFromResourceApi: 0,
      cssFromResourceApi: 0,
      fontFromResourceApi: 0,
    };

    for (let i = 0; i < document.length; i++) {
      document.styleSheets[i].href && browserData['resources'].cssFromStyleSheets++;
    }

    let resources = window.performance.getEntriesByType('resource');

    for (let i = 0; i < resources.length; i++) {
      const resource = resources[i];

      if ('img' === resource.initiatorType) {
        browserData['resources'].imgFromResourceApi++;
      }

      if ('link' === resource.initiatorType && -1 !== resource.name.indexOf('.woff')) {
        browserData['resources'].fontFromResourceApi++;
      }

      if (
        'css' === resource.initiatorType ||
        ('link' === resource.initiatorType && -1 !== resource.name.indexOf('.css'))
      ) {
        browserData['resources'].cssFromResourceApi++;
      }
    }
  });

  time('syncIntlDocumentNetwork', () => {
    try {
      if (window.Intl && typeof window.Intl.DateTimeFormat === 'function') {
        browserData['intl'] = {
          dateFormat: window.Intl.DateTimeFormat().resolvedOptions(),
          numberFormat: window.Intl.NumberFormat().resolvedOptions(),
          collator: window.Intl.Collator().resolvedOptions(),
        };
      } else {
        browserData['intl'] = null;
      }
    } catch (e) {
      browserData['intl'] = null;
      pushCollectIncident(browserData.incidents, { source: 'browser', phase: 'intl', error: e });
    }

    try {
      browserData.document = {
        dir: document.dir,
        characterSet: document.characterSet,
        visibilityState: document.visibilityState,
        hidden: document.hidden,
        hasFocus: typeof document.hasFocus === 'function' ? document.hasFocus() : null,
        fullscreen: !!document.fullscreenElement,
      };
    } catch (e) {
      browserData.document = null;
      pushCollectIncident(browserData.incidents, { source: 'browser', phase: 'document', error: e });
    }

    try {
      const conn = navigator.connection;
      if (conn) {
        browserData['network'] = {
          downlink: conn.downlink,
          downlinkMax: conn.downlinkMax,
          effectiveType: conn.effectiveType,
          rtt: conn.rtt,
          saveData: conn.saveData,
          type: conn.type,
        };
      } else {
        browserData['network'] = null;
      }
    } catch (e) {
      browserData['network'] = null;
      pushCollectIncident(browserData.incidents, { source: 'browser', phase: 'network', error: e });
    }
  });

  const [storage, media, specificAndSensors, keyboard, capabilities] = await asyncResultsPromise;
  browserData.storage = storage;
  browserData.media = media;
  browserData.specific = specificAndSensors.specific;
  browserData.sensors = specificAndSensors.sensors;
  browserData.keyboard = keyboard;
  browserData.capabilities = capabilities;
  if (otherDetails) {
    otherDetails.capabilities = capDetails;
  }

  return browserData;
}